import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Upload, FileText, Ban, ChevronDown, ChevronRight, CheckCircle2, Landmark } from "lucide-react";
import { API, authHeaders, money, inputCls, labelCls, Modal, VoidDialog, uploadFinanceFile } from "./shared";

const STATUS_STYLES = {
  pending: "bg-yellow-500/15 text-yellow-400",
  deposited: "bg-blue-500/15 text-blue-300",
  reconciled: "bg-green-500/15 text-green-400",
  voided: "bg-red-500/20 text-red-400",
};

export default function DepositsModule({ token, onAuthError, options }) {
  const [deposits, setDeposits] = useState([]);
  const [undeposited, setUndeposited] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [voiding, setVoiding] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([
      axios.get(`${API}/finance/deposits`, authHeaders(token)),
      axios.get(`${API}/finance/undeposited`, authHeaders(token)),
    ]).then(([d, u]) => { setDeposits(d.data || []); setUndeposited(u.data); })
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setStatus = async (dep, status) => {
    try {
      await axios.patch(`${API}/finance/deposits/${dep.id}/status`, { status }, authHeaders(token));
      toast.success(`${dep.number} marked ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const doVoid = async (reason) => {
    setBusy(true);
    try {
      await axios.post(`${API}/finance/deposits/${voiding.id}/void`, { reason }, authHeaders(token));
      toast.success(`${voiding.number} voided — payments released`);
      setVoiding(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not void"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>;

  return (
    <div className="space-y-4" data-testid="deposits-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-white font-bold">Bank Deposits</h3>
          <p className="text-white/45 text-sm">
            Reconcile money received with money deposited. <span className="text-yellow-400 font-semibold">{undeposited.items.length} payment(s) · {money(undeposited.total)}</span> awaiting deposit.
          </p>
        </div>
        <button data-testid="create-deposit-btn" onClick={() => setCreating(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold hover:bg-white transition-colors">
          <Plus size={15} /> Create Deposit
        </button>
      </div>

      <div className="space-y-2">
        {deposits.length === 0 && <p className="text-white/35 text-sm bg-[#0C0C0C] border border-white/10 rounded-xl p-5">No deposits created yet. Click "+ Create Deposit" to bundle received payments into a bank deposit.</p>}
        {deposits.map((d) => {
          const diff = Number(d.difference || 0);
          return (
            <div key={d.id} data-testid={`deposit-row-${d.id}`} className={`bg-[#0C0C0C] border rounded-xl overflow-hidden ${Math.abs(diff) >= 0.01 && d.status !== "voided" ? "border-red-500/40" : "border-white/10"} ${d.status === "voided" ? "opacity-50" : ""}`}>
              <button onClick={() => setOpenId(openId === d.id ? null : d.id)} className="w-full flex flex-wrap items-center gap-2 px-4 py-3 text-left hover:bg-white/[0.02]">
                {openId === d.id ? <ChevronDown size={15} className="text-white/40" /> : <ChevronRight size={15} className="text-white/40" />}
                <Landmark size={15} className="text-[#F2B705]" />
                <span className="font-semibold text-white">{d.number}</span>
                <span className="text-white/50 text-sm">{d.deposit_date} · {d.method}{d.bank_account ? ` · ${d.bank_account}` : ""}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${STATUS_STYLES[d.status] || ""}`}>{d.status}</span>
                {Math.abs(diff) >= 0.01 && d.status !== "voided" && (
                  <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/20 text-red-400 font-bold">{diff > 0 ? "+" : ""}{money(diff)} DIFFERENCE</span>
                )}
                <span className="ml-auto font-display font-bold text-lg text-white">{money(d.amount)}</span>
              </button>
              {openId === d.id && (
                <div className="border-t border-white/10 px-4 py-3 bg-[#0A0A0A]">
                  <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                    <div><div className="text-white/40 text-xs">Expected Deposit</div><div className="font-bold text-white">{money(d.expected)}</div></div>
                    <div><div className="text-white/40 text-xs">Actual Deposit</div><div className="font-bold text-white">{money(d.amount)}</div></div>
                    <div><div className="text-white/40 text-xs">Difference</div>
                      <div className={`font-bold ${Math.abs(diff) >= 0.01 ? "text-red-400" : "text-green-400"}`}>{Math.abs(diff) >= 0.01 ? `${diff > 0 ? "+" : ""}${money(diff)}` : "Balanced ✓"}</div></div>
                  </div>
                  <div className="text-white/40 text-xs mb-1.5 uppercase tracking-wider">Included Payments ({(d.included || []).length})</div>
                  <div className="divide-y divide-white/5 mb-3">
                    {(d.included || []).map((i) => (
                      <div key={i.ref} className="flex items-center gap-2 py-1.5 text-sm">
                        <span className="text-white/50 text-xs w-20">{i.date}</span>
                        <span className="text-white/80 truncate">{i.label}</span>
                        <span className="text-white/45 text-xs">{i.customer}</span>
                        <span className="text-white/40 text-xs">{i.method}</span>
                        <span className="ml-auto text-green-400 font-semibold">{money(i.amount)}</span>
                      </div>
                    ))}
                    {(d.included || []).length === 0 && <p className="text-white/30 text-xs py-1.5">No linked payments (amount-only deposit).</p>}
                  </div>
                  {d.reference && <div className="text-white/45 text-xs">Reference: {d.reference}</div>}
                  {d.notes && <div className="text-white/45 text-xs">Notes: {d.notes}</div>}
                  <div className="text-white/35 text-xs mt-0.5">Created by {d.entered_by}</div>
                  {d.status === "voided" && <div className="text-red-400/80 text-xs mt-1">Voided by {d.voided_by} — {d.void_reason}</div>}
                  <div className="flex items-center gap-2 mt-3">
                    {d.slip_path && (
                      <a href={`${API}/files/${d.slip_path}?auth=${token}`} target="_blank" rel="noreferrer"
                        className="text-[#F2B705] hover:underline flex items-center gap-1 text-xs"><FileText size={12} /> Deposit slip</a>
                    )}
                    {d.status === "pending" && (
                      <button data-testid={`dep-mark-deposited-${d.id}`} onClick={() => setStatus(d, "deposited")}
                        className="flex items-center gap-1 px-3 py-1.5 rounded bg-blue-500/15 text-blue-300 text-xs font-semibold hover:bg-blue-500/25"><CheckCircle2 size={12} /> Mark Deposited</button>
                    )}
                    {d.status === "deposited" && (
                      <button data-testid={`dep-mark-reconciled-${d.id}`} onClick={() => setStatus(d, "reconciled")}
                        className="flex items-center gap-1 px-3 py-1.5 rounded bg-green-500/15 text-green-400 text-xs font-semibold hover:bg-green-500/25"><CheckCircle2 size={12} /> Mark Reconciled</button>
                    )}
                    {d.status !== "voided" && (
                      <button data-testid={`dep-void-${d.id}`} onClick={() => setVoiding(d)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded text-white/50 text-xs hover:text-red-400"><Ban size={12} /> Void</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {creating && (
        <DepositForm token={token} options={options} undeposited={undeposited.items}
          onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
      {voiding && <VoidDialog label={`Deposit ${voiding.number}`} busy={busy} onConfirm={doVoid} onClose={() => setVoiding(null)} />}
    </div>
  );
}

function DepositForm({ token, options, undeposited, onClose, onSaved }) {
  const [form, setForm] = useState({ deposit_date: new Date().toISOString().slice(0, 10), amount: "", method: "Cash", bank_account: "", reference: "", notes: "" });
  const [selected, setSelected] = useState(new Set());
  const [slip, setSlip] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggle = (ref) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(ref) ? n.delete(ref) : n.add(ref);
    return n;
  });
  const expected = undeposited.filter((i) => selected.has(i.ref)).reduce((s, i) => s + i.amount, 0);
  const diff = Number(form.amount || 0) - expected;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { setSlip(await uploadFinanceFile(token, file)); toast.success("Deposit slip attached"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter the deposit amount"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/finance/deposits`, {
        ...form, amount: Number(form.amount), included_refs: [...selected],
        slip_path: slip?.path || null, slip_content_type: slip?.content_type || null, slip_filename: slip?.filename || null,
      }, authHeaders(token));
      toast.success("Deposit created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create deposit"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Create Bank Deposit" onClose={onClose} wide>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div><label className={labelCls}>Deposit Date</label>
          <input data-testid="dep-form-date" type="date" className={inputCls} value={form.deposit_date} onChange={(e) => set("deposit_date", e.target.value)} /></div>
        <div><label className={labelCls}>Deposit Amount *</label>
          <input data-testid="dep-form-amount" type="number" step="0.01" className={inputCls} value={form.amount} onChange={(e) => set("amount", e.target.value)} /></div>
        <div><label className={labelCls}>Deposit Method</label>
          <select data-testid="dep-form-method" className={inputCls} value={form.method} onChange={(e) => set("method", e.target.value)}>
            {(options?.deposit_methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
          </select></div>
        <div><label className={labelCls}>Bank Account</label>
          <input data-testid="dep-form-bank" className={inputCls} placeholder="e.g. Business Checking ••4821" value={form.bank_account} onChange={(e) => set("bank_account", e.target.value)} /></div>
        <div><label className={labelCls}>Reference #</label>
          <input data-testid="dep-form-reference" className={inputCls} value={form.reference} onChange={(e) => set("reference", e.target.value)} /></div>
        <div><label className={labelCls}>Notes</label>
          <input data-testid="dep-form-notes" className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5">
          <label className={labelCls + " !mb-0"}>Include Payments ({selected.size} selected)</label>
          <span className="text-xs text-white/50">Expected: <span className="text-white font-semibold">{money(expected)}</span></span>
        </div>
        <div className="max-h-56 overflow-y-auto border border-white/10 rounded-md divide-y divide-white/5" data-testid="dep-undeposited-list">
          {undeposited.length === 0 && <p className="text-white/35 text-xs p-3">No undeposited payments available.</p>}
          {undeposited.map((i) => (
            <label key={i.ref} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-white/[0.03]">
              <input type="checkbox" data-testid={`dep-include-${i.ref}`} checked={selected.has(i.ref)} onChange={() => toggle(i.ref)} className="accent-[#F2B705]" />
              <span className="text-white/50 text-xs w-20 shrink-0">{i.date}</span>
              <span className="text-white/80 truncate">{i.label}</span>
              <span className="text-white/45 text-xs truncate">{i.customer}</span>
              <span className="text-white/40 text-xs">{i.method}</span>
              <span className="ml-auto text-green-400 font-semibold shrink-0">{money(i.amount)}</span>
            </label>
          ))}
        </div>
        {form.amount && (
          <div className={`mt-2 text-sm font-semibold ${Math.abs(diff) >= 0.01 ? "text-red-400" : "text-green-400"}`} data-testid="dep-difference-preview">
            {Math.abs(diff) >= 0.01 ? `Difference: ${diff > 0 ? "+" : ""}${money(diff)} (actual vs expected)` : "Actual matches expected ✓"}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4">
        <label data-testid="dep-form-slip" className="inline-flex items-center gap-2 cursor-pointer border border-white/15 hover:border-[#F2B705]/50 rounded-md px-3 py-2 text-sm text-white/60 transition-colors">
          {uploading ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />}
          {slip ? "Deposit slip attached ✓" : "Attach deposit slip (optional)"}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} />
        </label>
        <button data-testid="dep-form-save" onClick={save} disabled={saving}
          className="ml-auto px-5 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold disabled:opacity-60">
          {saving ? "Creating…" : "Create Deposit"}
        </button>
      </div>
    </Modal>
  );
}
